import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import log from 'loglevel';

import { useUnsnoozeUserCommunication } from './useUnsnoozeUserCommunication';
import { useUpdateSnooze } from '../../../services/snoozedEmails.service';
import { useCustomToast } from '../../useCustomToast';

export const useEditSnoozeUserCommunication = () => {
  const { t } = useTranslation();
  const { updateSnooze, loading: isRescheduling } = useUpdateSnooze();
  const { handleUnsnoozeUserCommunication, isUnsnoozing } =
    useUnsnoozeUserCommunication();
  const { successMessage, errorMessage } = useCustomToast();

  const handleReschedule = useCallback(
    async (
      snoozeIds: string | string[],
      snoozeUntil: number,
      options?: { onSuccess?: () => void },
    ): Promise<boolean> => {
      const snoozeIdsArray = Array.isArray(snoozeIds) ? snoozeIds : [snoozeIds];

      if (snoozeIdsArray.length === 0) {
        return false;
      }

      try {
        const results = await Promise.all(
          snoozeIdsArray.map(async (snoozeId) => {
            try {
              const updated = await updateSnooze({ snoozeId, snoozeUntil });
              return { snoozeId, success: !!updated };
            } catch (error) {
              log.error('useEditSnoozeUserCommunication: updateSnooze failed', {
                snoozeId,
                error,
              });
              return { snoozeId, success: false };
            }
          }),
        );

        const succeeded = results.filter((r) => r.success);
        const failed = results.filter((r) => !r.success);

        if (failed.length === 0) {
          options?.onSuccess?.();
          successMessage(
            snoozeIdsArray.length === 1
              ? t('inbox.snooze.toast.updateSuccess')
              : t('inbox.snooze.toast.updateSuccessMultiple', {
                  count: succeeded.length,
                }),
          );
          return true;
        }

        if (succeeded.length > 0) {
          errorMessage(
            t('inbox.snooze.toast.updatePartialError', {
              failed: failed.length,
              total: snoozeIdsArray.length,
            }),
          );
        } else {
          errorMessage(t('inbox.snooze.toast.updateError'));
        }
        return false;
      } catch (error) {
        log.error('useEditSnoozeUserCommunication: unexpected failure', {
          error,
        });
        errorMessage(t('inbox.snooze.toast.updateError'));
        return false;
      }
    },
    [updateSnooze, successMessage, errorMessage, t],
  );

  const handleCancel = useCallback(
    async (snoozeId: string): Promise<boolean> => {
      const wasUnsnoozed = await handleUnsnoozeUserCommunication(snoozeId);

      if (wasUnsnoozed) {
        successMessage(t('inbox.snooze.toast.cancelSuccess'));
      } else {
        errorMessage(t('inbox.snooze.toast.cancelError'));
      }

      return wasUnsnoozed;
    },
    [handleUnsnoozeUserCommunication, successMessage, errorMessage, t],
  );

  return {
    handleReschedule,
    isRescheduling,
    handleCancel,
    isCanceling: isUnsnoozing,
  };
};
